/**
 * BusinessPricingColumn — la columna central de la web de precios (landing y
 * /pricing sin sesión), según la pantalla «Web · página de precios» de
 * `temp_docs/CardBusiness.html`.
 *
 * A diferencia de la card de la app, aquí no sabemos cuántas fichas tiene el
 * visitante: el stepper arranca en 1 y llega a 30, y el desglose solo aparece en
 * cuanto hay algo que desglosar (más de un local). En el tope sale el enlace a
 * Enterprise.
 *
 * Componente PURO, como `BusinessSubscriptionCard`: el contenedor decide qué hay
 * detrás del botón (en las páginas públicas, el registro).
 */
import { useId } from 'react';

import CtaLink from '@/components/CtaLink';
import { useLanguage } from '@/components/LanguageContext';
import { PRO_MAX_SEATS, cartBreakdown, localCurrencyCode } from '@/lib/priceBreakdown';
import { WEB_GROUPS } from './copy';
import {
  ACCENT, BoxTotal, BreakdownRow, CheckIcon, PeriodToggle, RichText, SeatsStepper, useMoney,
} from './parts';

export default function BusinessPricingColumn({
  pricing, seats, billing, onSeatsChange, onBillingChange, onCheckout,
  ctaId, trialDays = 0, trialAvailable = false, enterpriseLink = null, className = '',
}) {
  const { t, language } = useLanguage();
  const { money, hero, vat } = useMoney(pricing, language);
  const labelId = `${useId()}-seats`;

  const annual = billing === 'annual';
  const max = pricing?.maxSeats || PRO_MAX_SEATS;
  const b = cartBreakdown(pricing, billing, seats);
  const n = b.seats;
  const varios = n > 1;
  const pct = pricing?.annualDiscountPct ?? 20;
  const showTrial = trialAvailable && trialDays > 0;
  const localCurrency = localCurrencyCode(pricing?.displayCurrency);

  const period = t(annual ? 'billing.sub.perYear' : 'billing.sub.perMonth');
  const seatWord = t(n === 1 ? 'billing.sub.seatOne' : 'billing.sub.seatMany');

  let secondary = null;
  if (varios) {
    secondary = annual
      ? t('billing.sub.equivPerSeat', { month: money(b.unit / 12) })
      : t('billing.sub.perSeatWith', { n });
  } else if (annual) {
    const yearVat = vat(b.unit);
    secondary = yearVat
      ? t('billing.sub.equivMonthVatYear', { month: money(b.unit / 12), yearVat })
      : t('billing.sub.equivMonth', { month: money(b.unit / 12) });
  } else {
    const monthVat = vat(b.unit);
    if (monthVat) secondary = t('billing.sub.vatMonth', { price: monthVat });
  }

  const totalVat = vat(b.total);

  return (
    <section
      className={`flex min-w-0 flex-col gap-5 rounded-[22px] border-2 bg-white px-7 py-[30px] text-stone-900 shadow-[0_12px_40px_rgba(180,83,9,0.12)] ${className}`}
      style={{ borderColor: ACCENT }}
    >
      <div className="flex flex-col gap-1.5 text-center">
        <h2 className="m-0 text-xl font-extrabold tracking-[-0.01em]">MyTapstar {t('billing.card.planName')}</h2>
        <p className="m-0 text-[15px] leading-[1.45] text-stone-700">{t('billing.sub.web.tagline')}</p>
      </div>

      <PeriodToggle
        billing={billing}
        onChange={onBillingChange}
        monthlyLabel={t('billing.sub.monthly')}
        annualLabel={t('billing.sub.annual')}
        saveLabel={t('billing.sub.annualSave', { pct })}
        bestLabel={t('billing.sub.bestOption')}
        heightClass="min-h-[46px]"
      />

      <div className="flex items-center justify-between gap-4">
        <span id={labelId} className="text-[15px] font-semibold">{t('billing.sub.seatsQuestionWeb')}</span>
        <SeatsStepper
          n={n}
          max={max}
          onChange={onSeatsChange}
          labelledBy={labelId}
          decrementLabel={t('billing.sub.decrement')}
          incrementLabel={t('billing.sub.increment')}
          size="web"
        />
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span data-testid="sub-hero-price" className="text-[44px] font-extrabold leading-none tracking-[-0.03em] tabular-nums">
            {hero(b.unit)}
          </span>
          <span className="text-[15px] text-stone-600">{t('billing.sub.perSeatSuffix', { period })}</span>
        </div>
        {secondary && <div data-testid="sub-secondary" className="text-sm text-stone-600">{secondary}</div>}
        {localCurrency && (
          <div className="text-xs text-stone-500">
            {t('billing.card.localCurrencyNote', { currency: localCurrency })}
          </div>
        )}
      </div>

      {varios && (
        <div className="flex flex-col gap-2 rounded-[14px] border border-stone-200 bg-stone-50 px-4 py-3.5 tabular-nums">
          <div data-testid="sub-savings" className="text-[15px] font-bold text-green-700">
            {t(annual ? 'billing.sub.savingsYear' : 'billing.sub.savingsMonth', {
              amount: money(b.savings), pct: b.pct,
            })}
          </div>
          <BreakdownRow
            label={t(annual ? 'billing.sub.grossYear' : 'billing.sub.grossMonth', {
              n, seatWord, price: money(pricing?.totals?.monthly?.[0] || 0),
            })}
            value={money(b.gross)}
          />
          {annual && (
            <BreakdownRow label={t('billing.sub.annualDiscount', { pct })} value={`−${money(b.annualDiscount)}`} discount />
          )}
          <BreakdownRow
            label={t('billing.sub.volumeDiscount', { n, seatWord })}
            value={`−${money(b.volumeDiscount)}`}
            discount
          />
          <BoxTotal
            label={t('billing.sub.total')}
            total={money(b.total)}
            suffix={t(annual ? 'billing.sub.plusVatYear' : 'billing.sub.plusVatMonth')}
            vatLine={totalVat ? t(annual ? 'billing.sub.vatYear' : 'billing.sub.vatMonth', { price: totalVat }) : null}
          />
        </div>
      )}

      {n >= max && enterpriseLink}

      <div className="flex flex-col gap-2.5">
        <CtaLink
          as="button"
          cta={ctaId}
          type="button"
          onClick={onCheckout}
          style={{ background: ACCENT }}
          className="flex min-h-[54px] w-full cursor-pointer items-center justify-center rounded-[14px] border-0 font-[inherit] text-[17px] font-bold text-stone-900 transition-[filter] hover:brightness-95"
        >
          {showTrial ? t('billing.sub.ctaTrial', { days: trialDays }) : t('billing.card.ctaNoTrial')}
        </CtaLink>
        <p className="m-0 text-center text-[13px] leading-normal text-stone-600">
          {showTrial ? t('billing.sub.fineTrial') : t('billing.card.footerNoTrial')}
        </p>
      </div>

      <div className="flex flex-col gap-[18px] border-t border-stone-200 pt-5">
        <p className="m-0 text-[15px] font-bold">{t('billing.sub.web.intro')}</p>
        {WEB_GROUPS.map((g) => (
          <div key={g.title} className="flex flex-col gap-2.5">
            <h4 className="m-0 text-[13px] font-bold text-stone-900">{t(g.title)}</h4>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {g.items.map((k) => (
                <li key={k} className="flex items-start gap-2.5 text-sm leading-[1.45] text-stone-700">
                  <CheckIcon size={16} strokeWidth={2.6} className="mt-0.5" />
                  <span className="[&_strong]:text-stone-900"><RichText i18nKey={k} /></span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
